/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink as TabLink,
  Row,
  Table,
  TabContent,
  TabPane,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import AppLayout from "layout/AppLayout";
import axios from "axiosClient";
import ConfirmModal from "components/ConfirmModal";
import Pagination from "containers/pages/Pagination";
import SelectInput from "components/common/SelectInput";
import { formatGmt5, GMT5_LABEL } from "utils/timezone";
import {
  AUDIT_ACTION_LABELS,
  StatusBadge,
  TypeBadge,
  USAGE_STATUS_META,
  discountLabel,
  money,
  scopeLabel,
  usageLabel,
} from "./shared";

const PAGE_SIZE = 30;

const Field = ({ label, children }) => (
  <Col md="4" className="mb-3">
    <p className="text-muted mb-1 text-small">{label}</p>
    <p className="mb-0">{children}</p>
  </Col>
);

const PromocodeView = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [tab, setTab] = useState("overview");
  const [promocode, setPromocode] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [archiveModal, setArchiveModal] = useState(false);
  const [restoreModal, setRestoreModal] = useState(false);

  const [usages, setUsages] = useState([]);
  const [usageFilters, setUsageFilters] = useState({ search: "", status: "" });
  const [usagePage, setUsagePage] = useState(1);
  const [usageTotalPage, setUsageTotalPage] = useState(1);
  const [usageTotal, setUsageTotal] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);

  const [audit, setAudit] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPage, setAuditTotalPage] = useState(1);

  const loadPromocode = useCallback(() => {
    setLoading(true);
    axios
      .get(`/api/promocode/${id}`)
      .then((result) => {
        setPromocode(result.data.promocode);
        setStats(result.data.stats);
      })
      .catch(() => toast.error("Could not load this promocode"))
      .finally(() => setLoading(false));
  }, [id]);

  const loadUsages = useCallback(
    (page = 1, filters = usageFilters) => {
      axios
        .post("/api/promocode-usages", {
          promocodeId: id,
          page,
          per_page: PAGE_SIZE,
          ...filters,
        })
        .then((result) => {
          setUsages(result.data.items);
          setUsageTotal(result.data.total);
          setUsagePage(result.data.page);
          setUsageTotalPage(result.data.pageCount);
          setTotalDiscount(result.data.totalDiscount);
        })
        .catch(() => toast.error("Could not load the usage list"));
    },
    [id, usageFilters],
  );

  const loadAudit = useCallback(
    (page = 1) => {
      axios
        .post("/api/promocode-audit", {
          promocodeId: id,
          page,
          per_page: PAGE_SIZE,
        })
        .then((result) => {
          setAudit(result.data.items);
          setAuditPage(result.data.page);
          setAuditTotalPage(result.data.pageCount);
        })
        .catch(() => toast.error("Could not load the audit log"));
    },
    [id],
  );

  useEffect(() => {
    loadPromocode();
    loadUsages(1);
    loadAudit(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleActive = () => {
    axios
      .post(`/api/promocode/${id}/status`, { isActive: !promocode.isActive })
      .then(() => {
        toast.success(promocode.isActive ? "Deactivated" : "Activated");
        loadPromocode();
        loadAudit(1);
      })
      .catch((err) =>
        toast.error(err.response?.data?.message || "Could not update"),
      );
  };

  const archive = () => {
    axios
      .delete(`/api/promocode/${id}`)
      .then(() => {
        toast.success("Promocode archived");
        loadPromocode();
        loadAudit(1);
      })
      .catch((err) =>
        toast.error(err.response?.data?.message || "Could not archive"),
      );
  };

  const restore = () => {
    axios
      .post(`/api/promocode/${id}/restore`)
      .then(() => {
        toast.success("Promocode restored");
        loadPromocode();
        loadAudit(1);
      })
      .catch((err) =>
        toast.error(err.response?.data?.message || "Could not restore"),
      );
  };

  const voidUsage = (usage) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      `Why is this usage by ${usage.candidate?.fullName || "this candidate"} being voided?`,
    );
    if (!reason || !reason.trim()) return;

    axios
      .post(`/api/promocode-usage/${usage._id}/void`, { reason: reason.trim() })
      .then(() => {
        toast.success("Usage voided");
        loadPromocode();
        loadUsages(usagePage);
        loadAudit(1);
      })
      .catch((err) =>
        toast.error(err.response?.data?.message || "Could not void this usage"),
      );
  };

  const downloadUsages = () => {
    axios
      .post(
        "/api/promocode-usages-download",
        { promocodeId: id, ...usageFilters },
        { responseType: "blob" },
      )
      .then((result) => {
        const url = window.URL.createObjectURL(new Blob([result.data]));
        const link = document.createElement("a");
        link.href = url;
        link.download = `promocode-${promocode?.code || id}-usage.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => toast.error("Could not download the usage list"));
  };

  if (loading || !promocode) {
    return (
      <AppLayout>
        <div className="dashboard-wrapper">
          <div className="loading" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="dashboard-wrapper">
        <Card className="h-100 m-4">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start flex-wrap">
              <div>
                <CardTitle className="mb-1">
                  {promocode.code} <StatusBadge status={promocode.status} />
                </CardTitle>
                <p className="text-muted mb-0">
                  {promocode.description || "No description"}
                </p>
              </div>
              <div className="mt-2">
                <Button
                  color="light"
                  className="mr-2"
                  onClick={() => navigate("/app/promocodes")}
                >
                  Back
                </Button>
                {!promocode.isDeleted && (
                  <>
                    <Button
                      color="light"
                      className="mr-2"
                      onClick={() => navigate(`/app/promocodes/${id}/edit`)}
                    >
                      Edit
                    </Button>
                    <Button
                      color={promocode.isActive ? "warning" : "success"}
                      className="mr-2 text-white"
                      onClick={toggleActive}
                    >
                      {promocode.isActive ? "Deactivate" : "Activate"}
                    </Button>
                    <Button
                      color="danger"
                      onClick={() => setArchiveModal(true)}
                    >
                      Archive
                    </Button>
                  </>
                )}
                {promocode.isDeleted && (
                  <Button
                    color="primary"
                    onClick={() => setRestoreModal(true)}
                  >
                    Restore
                  </Button>
                )}
              </div>
            </div>

            <Nav tabs className="separator-tabs mt-4 mb-4">
              {[
                { key: "overview", label: "Overview" },
                { key: "usage", label: `Usage (${usageTotal})` },
                { key: "audit", label: "Audit log" },
              ].map((item) => (
                <NavItem key={item.key}>
                  <TabLink
                    href="#"
                    className={tab === item.key ? "active" : ""}
                    onClick={(e) => {
                      e.preventDefault();
                      setTab(item.key);
                    }}
                  >
                    {item.label}
                  </TabLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={tab}>
              <TabPane tabId="overview">
                <Row className="text-left">
                  <Field label="Works at">
                    <TypeBadge type={promocode.type} />
                  </Field>
                  <Field label="Discount">{discountLabel(promocode)}</Field>
                  <Field label="Minimum exam price">
                    {promocode.minOrderAmount
                      ? money(promocode.minOrderAmount)
                      : "No minimum"}
                  </Field>
                  <Field label="Applies to">{scopeLabel(promocode)}</Field>

                  <Field label="Can be used">
                    {promocode.usageType === "single"
                      ? "Once only"
                      : promocode.unlimited
                        ? "Multiple times, unlimited quantity"
                        : `Multiple times, ${promocode.usageLimit} in total`}
                  </Field>
                  <Field label="Uses per candidate">
                    {promocode.usageLimitPerCandidate === 0
                      ? "Unlimited"
                      : promocode.usageLimitPerCandidate}
                  </Field>
                  <Field label="Used">
                    {usageLabel(promocode)}
                    {promocode.reservedCount > 0 && (
                      <span className="text-muted text-small">
                        {" "}
                        ({promocode.reservedCount} in progress)
                      </span>
                    )}
                  </Field>

                  <Field label={`Valid from (${GMT5_LABEL})`}>
                    {formatGmt5(promocode.startsAt)}
                  </Field>
                  <Field label={`Valid until (${GMT5_LABEL})`}>
                    {formatGmt5(promocode.endsAt)}
                  </Field>
                  <Field label="Total discount given">
                    {money(stats?.totalDiscountGiven)}
                  </Field>

                  <Field label="Created by">
                    {promocode.createdBy?.username || "—"}
                    <br />
                    <span className="text-muted text-small">
                      {formatGmt5(promocode.createdAt)}
                    </span>
                  </Field>
                  <Field label="Last edited by">
                    {promocode.updatedBy?.username || "—"}
                    <br />
                    <span className="text-muted text-small">
                      {formatGmt5(promocode.updatedAt)}
                    </span>
                  </Field>
                  {promocode.isDeleted && (
                    <Field label="Archived by">
                      {promocode.deletedBy?.username || "—"}
                      <br />
                      <span className="text-muted text-small">
                        {formatGmt5(promocode.deletedAt)}
                      </span>
                    </Field>
                  )}
                </Row>
              </TabPane>

              <TabPane tabId="usage">
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    loadUsages(1, usageFilters);
                  }}
                >
                  <Row className="text-left">
                    <Col md="4">
                      <FormGroup>
                        <Label>Candidate, PINPP, enquiry or transaction</Label>
                        <Input
                          type="text"
                          value={usageFilters.search}
                          onChange={(e) =>
                            setUsageFilters({
                              ...usageFilters,
                              search: e.target.value,
                            })
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Status</Label>
                        <SelectInput
                          placeholder="All"
                          isClearable
                          value={usageFilters.status}
                          onChange={(status) =>
                            setUsageFilters({ ...usageFilters, status })
                          }
                          options={[
                            { label: "Applied", value: "applied" },
                            { label: "Reserved", value: "reserved" },
                            { label: "Released", value: "released" },
                          ]}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="5" className="d-flex align-items-end mb-3">
                      <Button
                        color="primary"
                        className="mr-2"
                        onClick={() => loadUsages(1, usageFilters)}
                      >
                        Search
                      </Button>
                      <Button color="light" onClick={downloadUsages}>
                        Download .xlsx
                      </Button>
                    </Col>
                  </Row>
                </Form>

                <p className="text-muted text-small">
                  {usageTotal} record(s) · {money(totalDiscount)} discounted in
                  total
                </p>

                <Table responsive className="r-table table text-left">
                  <thead>
                    <tr>
                      <th>Candidate</th>
                      <th>PINPP / Passport</th>
                      <th>Exam</th>
                      <th>Discount</th>
                      <th>Paid</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>{`When (${GMT5_LABEL})`}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {usages.length === 0 && (
                      <tr>
                        <td colSpan="9" className="text-muted">
                          Nobody has used this promocode yet.
                        </td>
                      </tr>
                    )}
                    {usages.map((usage) => {
                      const meta =
                        USAGE_STATUS_META[usage.status] ||
                        USAGE_STATUS_META.released;
                      return (
                        <tr key={usage._id}>
                          <td>
                            {usage.candidate?.fullName || "—"}
                            <br />
                            <span className="text-muted text-small">
                              {usage.candidate?.email}
                            </span>
                          </td>
                          <td className="text-muted">
                            {usage.candidate?.pinpp || "—"}
                            <br />
                            <span className="text-small">
                              {usage.candidate?.passportId}
                            </span>
                          </td>
                          <td className="text-muted">
                            {usage.exam}
                            <br />
                            <span className="text-small">{usage.module}</span>
                          </td>
                          <td>{money(usage.discountAmount)}</td>
                          <td className="text-muted">
                            {money(usage.finalAmount)}
                            <br />
                            <span className="text-small">
                              was {money(usage.baseAmount)}
                            </span>
                          </td>
                          <td className="text-muted text-small">
                            {usage.paymentMethod || "—"}
                            <br />
                            {usage.transactionId || usage.enquiryId || ""}
                          </td>
                          <td>
                            <Badge color={meta.color} className="text-white">
                              {meta.label}
                            </Badge>
                            {usage.releaseReason && (
                              <div className="text-muted text-small">
                                {usage.releaseReason}
                              </div>
                            )}
                          </td>
                          <td className="text-muted text-small">
                            {formatGmt5(
                              usage.appliedAt ||
                                usage.releasedAt ||
                                usage.createdAt,
                              "DD/MM/YYYY HH:mm:ss",
                            )}
                          </td>
                          <td>
                            {(usage.status === "applied" ||
                              usage.status === "reserved") && (
                              <Button
                                size="xs"
                                color="light"
                                onClick={() => voidUsage(usage)}
                              >
                                Void
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                <Pagination
                  currentPage={usagePage}
                  totalPage={usageTotalPage}
                  onChangePage={(page) => loadUsages(page, usageFilters)}
                />
              </TabPane>

              <TabPane tabId="audit">
                <Table responsive className="r-table table text-left">
                  <thead>
                    <tr>
                      <th>{`When (${GMT5_LABEL})`}</th>
                      <th>Action</th>
                      <th>By</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-muted">
                          Nothing recorded yet.
                        </td>
                      </tr>
                    )}
                    {audit.map((entry) => (
                      <tr key={entry._id}>
                        <td className="text-muted text-small">
                          {formatGmt5(entry.createdAt, "DD/MM/YYYY HH:mm:ss")}
                        </td>
                        <td>
                          {AUDIT_ACTION_LABELS[entry.action] || entry.action}
                        </td>
                        <td className="text-muted">
                          {entry.actor?.username ||
                            entry.actor?.name ||
                            entry.actor?.kind ||
                            "system"}
                        </td>
                        <td className="text-small text-muted">
                          {entry.changes?.length > 0 && (
                            <ul className="mb-0 pl-3">
                              {entry.changes.map((change) => (
                                <li key={change.field}>
                                  <strong>{change.field}</strong>:{" "}
                                  {`${change.from || "—"}`} →{" "}
                                  {`${change.to || "—"}`}
                                </li>
                              ))}
                            </ul>
                          )}
                          {entry.meta?.reason && (
                            <div>Reason: {entry.meta.reason}</div>
                          )}
                          {entry.meta?.discountAmount != null && (
                            <div>
                              Discount: {money(entry.meta.discountAmount)}
                              {entry.meta.enquiryId
                                ? ` · enquiry ${entry.meta.enquiryId}`
                                : ""}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Pagination
                  currentPage={auditPage}
                  totalPage={auditTotalPage}
                  onChangePage={(page) => loadAudit(page)}
                />
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </div>

      <ConfirmModal
        modal={archiveModal}
        setModal={setArchiveModal}
        title={`Archive ${promocode.code}?`}
        body="The promocode stops working immediately. Its usage list and audit log are kept."
        action={archive}
        confirmLabel="Archive promocode"
        cancelLabel="Keep active"
        confirmColor="danger"
      />
      <ConfirmModal
        modal={restoreModal}
        setModal={setRestoreModal}
        title={`Restore ${promocode.code}?`}
        body="The promocode becomes available again, but stays deactivated until you activate it."
        action={restore}
        confirmLabel="Restore promocode"
      />
    </AppLayout>
  );
};

export default PromocodeView;
